import { alpha, keyframes, styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { selectProposalEnvelope } from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { latestIncomingProposalFieldChangeSequence } from "work/proposals/negotiation/proposal-incoming-field-change";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/** How long a field stays marked after someone else's change lands on it. */
export const proposalIncomingChangeHighlightDurationMs = 3000;

const incomingChangeFade = keyframes`
  0% { opacity: 0; }
  10% { opacity: 1; }
  65% { opacity: 1; }
  100% { opacity: 0; }
`;

/**
 * A tint laid over the field's value. It is deliberately a sibling overlay rather than a style on the
 * control itself: `position: absolute` inside the value container keeps it out of the layout, and
 * `pointer-events: none` plus `aria-hidden` keep it out of the way of the pointer, the focus order and
 * the accessibility tree — the value underneath stays exactly as clickable and as readable as it was.
 */
const IncomingChangeTint = styled("span", { shouldForwardProp: (prop) => prop !== "animate" })<{
  animate: boolean;
}>(({ theme, animate }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.22),
  borderRadius: theme.shape.borderRadius,
  bottom: 0,
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  right: 0,
  top: 0,
  zIndex: 0,
  ...(animate
    ? { animation: `${incomingChangeFade} ${proposalIncomingChangeHighlightDurationMs}ms ease-out forwards` }
    : {}),
}));

/**
 * Whether this viewer has asked for less motion. Read when a highlight mounts rather than subscribed
 * to: the mark lives for three seconds, so a preference changed mid-flash has nothing to correct.
 */
function prefersReducedMotion(): boolean {
  const preference = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)");
  return preference !== undefined && preference.matches;
}

type ProposalIncomingChangeHighlightProps = {
  field: ProposalField | null | undefined;
  negotiationId?: string;
  /** Defaults to the signed-in user; a change this user authored is never incoming. */
  currentUserId?: string;
};

/**
 * Marks a field for a moment when the other party (or a teammate) changes its value while the viewer
 * has the proposal open, so a value that moves under them is noticed rather than silently swapped.
 *
 * Renders nothing until the negotiation stream exists — the many field surfaces that render without a
 * negotiation behind them (a draft before its first save) have no collaboration state to read, exactly
 * as {@link ProposalFieldChangeBadge} does. Place it inside the field's `position: relative` value
 * container.
 */
export default function ProposalIncomingChangeHighlight({
  field,
  negotiationId,
  currentUserId,
}: Readonly<ProposalIncomingChangeHighlightProps>) {
  if (!negotiationId || !field) return null;

  return (
    <ConnectedProposalIncomingChangeHighlight
      field={field}
      negotiationId={negotiationId}
      currentUserId={currentUserId}
    />
  );
}

function ConnectedProposalIncomingChangeHighlight({
  field,
  negotiationId,
  currentUserId: explicitCurrentUserId,
}: Readonly<{ field: ProposalField; negotiationId: string; currentUserId?: string }>) {
  const session = useSession();
  const currentUserId = explicitCurrentUserId ?? session.user?.id?.value;
  const envelope = useSelector(selectProposalEnvelope(negotiationId));
  const streamVersion = envelope?.streamVersion;
  const incomingSequence = latestIncomingProposalFieldChangeSequence(envelope, field, currentUserId);

  // The newest stream version this field has already reacted to; undefined until an envelope loads.
  const [observedStreamVersion, setObservedStreamVersion] = useState<number | undefined>(undefined);
  // The sequence currently being marked; 0 while nothing is marked.
  const [markedSequence, setMarkedSequence] = useState(0);

  // Adjusted while rendering rather than in an effect, so the first envelope only ever establishes the
  // baseline and the mark appears in the same paint as the value it belongs to. Everything already on
  // the stream when the proposal opened is history the viewer is reading, not a value moving under
  // them, and must never flash the whole form on load.
  const observed = observedStreamVersion;
  let marked = markedSequence;
  if (streamVersion !== undefined && (observed === undefined || streamVersion > observed)) {
    if (observed !== undefined && incomingSequence > observed) {
      // Read below in the same pass, so the mark paints with the value rather than a frame after it.
      marked = incomingSequence;
      setMarkedSequence(incomingSequence);
    }
    setObservedStreamVersion(streamVersion);
  }

  useEffect(() => {
    if (markedSequence === 0) return;
    const timeout = globalThis.setTimeout(() => setMarkedSequence(0), proposalIncomingChangeHighlightDurationMs);
    return () => globalThis.clearTimeout(timeout);
  }, [markedSequence]);

  if (marked === 0) return null;

  const animate = !prefersReducedMotion();
  return (
    <IncomingChangeTint
      animate={animate}
      aria-hidden="true"
      data-testid="proposal-incoming-change-highlight"
      data-proposal-incoming-change={animate ? "animated" : "static"}
    />
  );
}
